import React from "react";
import Layout from "../../components/layout/Layout";
import { Tabs, Tab, Box } from "@mui/material";
import Loading from "../../components/customHelpers/Loading";
import Order from "../../actions/order";
import Notify from "../../components/customHelpers/Notify";
import Staff from "../../actions/staff";
import Product from "../../actions/product";
import Customer from "../../actions/customer";
import CustomerChart from "../../components/forms/CustomerChart";
import CustomerCountChart from "../../components/forms/CustomerCountChart";
import { isAuth } from "../../actions/auth";
import { useHistory } from "react-router-dom";
import { Package, Users, UserCircle, Store, ShoppingCart } from "lucide-react";
import SubscriptionBanner from "../../components/banner";

const Dashboard = () => {
  const { viewAllOrders } = Order();
  const { NotifyMessage, notify, setNotify } = Notify();
  const [customers, setCustomers] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const { totalStaff } = Staff();
  const { viewTotalProducts } = Product();
  const { viewTotalCustomers, viewAllCustomers } = Customer();
  const [staffNum, setStaffNum] = React.useState(0);
  const [customer, setCustomer] = React.useState(0);
  const [products, setProducts] = React.useState(0);
  const history = useHistory();
  const authData = isAuth();

  const allPermissions = isAuth()?.staffRoles?.flatMap(
    (staffRole) => staffRole.role?.rolePermissions || []
  );
  const hasGeneralSettingPermission = allPermissions?.some(
    (permission) => permission.action === "generalSetting"
  );

  const hasProductsPermission = allPermissions?.some(
    (permission) => permission.action === "listAllProducts"
  );
  const hasManageStaffPermission = allPermissions?.some(
    (permission) => permission.action === "manageStaff"
  );

  const hasCustomerPermission = allPermissions?.some(
    (permission) => permission.action === "viewCustomer"
  );
  React.useEffect(() => {
    if (isAuth()?.role === "superadmin") {
      history.push("/admin");
    }
    fetchOrders();
    getAllCustomers();
    totalStaff().then((data) => {
      setStaffNum(data.result);
    });
    viewTotalCustomers().then((data) => {
      setCustomer(data.result);
    });
    viewTotalProducts().then((data) => {
      if (data.result) {
        setProducts(data);
      }
    });
  }, []);

  const fetchOrders = () => {
    setLoading(true);
    viewAllOrders().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else {
         const filteredOrders = data?.orders?.filter(
          (order) => order.status !== "Pending"
        );
        setOrders(filteredOrders);
      }
      setLoading(false);
    });
  };

  const getAllCustomers = () => {
    setLoading(true);
    viewAllCustomers().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
        setLoading(false);
      } else if (data.customers) {
        setCustomers(data.customers);
      }
      setLoading(false);
    });
  };

  const filterOrdersByDate = (period) => {
    const now = new Date();
    const startDate = new Date();

    switch (period) {
      case "week":
        startDate.setDate(now.getDate() - 7);
        break;
      case "month":
        startDate.setDate(now.getDate() - 30);
        break;
      case "year":
        startDate.setMonth(now.getMonth() - 12);
        break;
      default:
        return [];
    }

    return orders?.filter((order) => new Date(order.createdAt) >= startDate);
  };

  const groupOrdersByCustomer = (ordersList) => {
    const customerOrders = {};

    ordersList?.forEach((order) => {
      const customerId = order?.customer?.id;
      const customerName = order?.customer?.fullName;

      let orderTotal = 0;
      if (order?.orderProducts?.length > 0) {
        order.orderProducts.forEach((product) => {
          orderTotal += parseFloat(product.totalPrice);
        });
      }

      if (!customerOrders[customerId]) {
        customerOrders[customerId] = { total: 0, fullName: customerName };
      }

      customerOrders[customerId].total += orderTotal;
    });

    return Object.entries(customerOrders)
      .map(([id, data]) => ({
        id,
        fullName: data.fullName,
        total: parseFloat(data.total).toFixed(2),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  };

  const top5CustomersWeek = groupOrdersByCustomer(filterOrdersByDate("week"));
  const top5CustomersMonth = groupOrdersByCustomer(filterOrdersByDate("month"));
  const top5CustomersYear = groupOrdersByCustomer(filterOrdersByDate("year"));

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const groupCustomersByDay = (customersList, days, useDayNames = false) => {
    const now = new Date();
    const dailyData = new Map();

    const startDate = new Date(now);
    startDate.setDate(now.getDate() - (days - 1));

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dailyData.set(date.toISOString().split("T")[0], 0);
    }

    customersList?.forEach((customer) => {
      const createdAt = new Date(customer.createdAt);
      const dateStr = createdAt.toISOString().split("T")[0];
      if (dailyData.has(dateStr)) {
        dailyData.set(dateStr, (dailyData.get(dateStr) || 0) + 1);
      }
    });

    const labels = Array.from({ length: days }, (_, i) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (useDayNames) {
        return date.toLocaleDateString("en-US", { weekday: "short" });
      } else {
        return date.getDate().toString();
      }
    });

    return labels.map((day, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const dateStr = date.toISOString().split("T")[0];
      return {
        day: day,
        count: dailyData.get(dateStr) || 0,
      };
    });
  };
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const groupCustomersByMonth = (customersList) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthlyData = Array(12).fill(0);

    customersList?.forEach((customer) => {
      const createdAt = new Date(customer.createdAt);
      if (isNaN(createdAt)) return;
      const month = createdAt.getMonth();
      const year = createdAt.getFullYear();
      const monthsDiff = (currentYear - year) * 12 + (currentMonth - month);
      if (monthsDiff >= 0 && monthsDiff < 12) {
        const index = 11 - monthsDiff;
        monthlyData[index] += 1;
      }
    });

    const labels = Array.from({ length: 12 }, (_, i) => {
      const monthsBack = 11 - i;
      const totalMonths = (currentMonth - monthsBack + 12) % 12;
      return monthNames[totalMonths];
    });
    return labels.map((month, index) => ({
      month,
      count: monthlyData[index],
    }));
  };
  const customerCountWeek = groupCustomersByDay(customers, 7, true);
  const customerCountMonth = groupCustomersByDay(customers, 30);
  const customerCountYear = groupCustomersByMonth(customers);

  const handleNavigate = (path) => {
    history.push(path);
  };

  return (
    <Layout>
      <SubscriptionBanner />
      <div className="dashboard-stats-card">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-icon-wrapper">
              <ShoppingCart className="stat-icon" />
            </div>
            <div
              onClick={
                hasProductsPermission || authData.role === "admin"
                  ? () => handleNavigate("/products")
                  : undefined
              }
              style={{
                cursor:
                  hasProductsPermission || authData.role === "admin"
                    ? "pointer"
                    : "not-allowed",
                opacity:
                  hasProductsPermission || authData.role === "admin" ? 1 : 0.5,
              }}
              className="stat-content"
            >
              <div className="stat-label">Products</div>
              <div className="stat-value">{products?.count?.products}</div>
            </div>
          </div>
          <div
            onClick={
              hasManageStaffPermission || authData.role === "admin"
                ? () => handleNavigate("/staffs")
                : undefined
            }
            style={{
              cursor:
                hasManageStaffPermission || authData.role === "admin"
                  ? "pointer"
                  : "not-allowed",
              opacity:
                hasManageStaffPermission || authData.role === "admin" ? 1 : 0.5,
            }}
            className="stat-item"
          >
            <div className="stat-icon-wrapper">
              <UserCircle className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Staff</div>
              <div className="stat-value">{staffNum}</div>
            </div>
          </div>

          <div
            onClick={
              hasCustomerPermission || authData.role === "admin"
                ? () => handleNavigate("/customers")
                : undefined
            }
            style={{
              cursor:
                hasCustomerPermission || authData.role === "admin"
                  ? "pointer"
                  : "not-allowed",
              opacity:
                hasCustomerPermission || authData.role === "admin" ? 1 : 0.5,
            }}
            className="stat-item"
          >
            <div className="stat-icon-wrapper">
              <Users className="stat-icon" />
            </div>
            <div className=" stat-content">
              <div className="stat-label">Customers</div>
              <div className="stat-value">{customer}</div>
            </div>
          </div>
          <div
            onClick={
              hasGeneralSettingPermission || authData.role === "admin"
                ? () => handleNavigate("/settings")
                : undefined
            }
            style={{
              cursor:
                hasGeneralSettingPermission || authData.role === "admin"
                  ? "pointer"
                  : "not-allowed",
              opacity:
                hasGeneralSettingPermission || authData.role === "admin"
                  ? 1
                  : 0.5,
            }}
            className="stat-item"
          >
            <div className="stat-icon-wrapper">
              <Store className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-label">Stores</div>
              <div className="stat-value">
                {hasGeneralSettingPermission || authData.role === "admin"
                  ? products?.count?.stores
                  : null}
              </div>
            </div>
          </div>
          <div
            onClick={
              hasProductsPermission || authData.role === "admin"
                ? () => handleNavigate("/products")
                : undefined
            }
            style={{
              cursor:
                hasProductsPermission || authData.role === "admin"
                  ? "pointer"
                  : "not-allowed",
              opacity:
                hasProductsPermission || authData.role === "admin" ? 1 : 0.5,
            }}
            className="stat-item"
          >
            <div className="stat-icon-wrapper">
              <Package className="stat-icon" />
            </div>
            <div className="stat-content">
              <div className="stat-label">No of Products</div>
              <div className="stat-value">{products?.result}</div>
            </div>
          </div>
        </div>
      </div>

      <Box sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="customer tabs"
        >
          <Tab label="Last Week" />
          <Tab label="Last Month" />
          <Tab label="Last Year" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Loading />
          ) : (
            <div className="charts-container">
              {tabValue === 0 && (
                <div className="chart-row">
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        Top 5 Customers Highest Order (Last 7 Days)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerChart data={top5CustomersWeek} />
                    </div>
                  </div>
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        New Customers Count (Last 7 Days)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerCountChart
                        data={customerCountWeek}
                        xAxisKey="day"
                        xLabel="Days"
                      />
                    </div>
                  </div>
                </div>
              )}
              {tabValue === 1 && (
                <div className="chart-row">
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        Top 5 Customers Highest Order (Last 30 Days)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerChart data={top5CustomersMonth} />
                    </div>
                  </div>
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        New Customers Count (Last 30 Days)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerCountChart
                        data={customerCountMonth}
                        xAxisKey="day"
                        xLabel="Days"
                      />
                    </div>
                  </div>
                </div>
              )}
              {tabValue === 2 && (
                <div className="chart-row">
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        Top 5 Customers Highest Order (Last 12 Months)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerChart data={top5CustomersYear} />
                    </div>
                  </div>
                  <div className="chart-item">
                    <div className="chart-header">
                      <h3 className="chart-title">
                        New Customers Count (Last 12 Months)
                      </h3>
                    </div>
                    <div className="chart-content">
                      <CustomerCountChart
                        data={customerCountYear}
                        xAxisKey="month"
                        xLabel="Months"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default Dashboard;
